import type { PortalHelpConfig } from './portalHelpTypes';

export const ADMIN_HELP: PortalHelpConfig = {
  pageTitle: 'Centre d’aide administration',
  pageDescription: 'Référentiel des modules admin : inscriptions, académique, finances, sécurité…',
  roleLabel: 'Administrateur',
  intro:
    'Documentation opérationnelle de tous les modules d’administration JCONSULT MY Academia.',
  gettingStarted: {
    title: 'Routine administrateur',
    steps: [
      'Connectez-vous et ouvrez le Tableau de bord : repérez les alertes (inscriptions en attente, dossiers bloquants, trésorerie).',
      'Traitez Inscriptions : validez ou refusez après contrôle paiement + dossier.',
      'Passez par Validation dossiers pour les pièces en attente.',
      'Vérifiez Planning et Formateurs si des sessions manquent d’animateur.',
      'Consultez Sécurité & traçabilité en fin de journée pour les actions sensibles de l’équipe.',
    ],
  },
  sections: [
    {
      id: 'dashboard',
      title: 'Tableau de bord',
      icon: 'dashboard',
      summary:
        'Vue consolidée : KPI inscriptions, académique, finances, RH formateurs et logistique. Point d’entrée pour détecter les anomalies avant d’ouvrir chaque module.',
      steps: [
        'Lisez les cartes KPI en haut : chaque chiffre est cliquable ou lié à un module détaillé via « Voir le module ».',
        'Comparez les tendances sur 14 jours (graphiques) pour anticiper les pics d’inscription ou de trésorerie.',
        'Section Académique : moyennes, présences — utile avant les conseils de cohorte.',
        'Section Finances : recettes et projections — croisez avec Paiements si un écart apparaît.',
        'Section RH formateurs : top formateurs, sessions animées — repère les surcharges ou les inactivités.',
        'Actualisez via la barre supérieure après une opération massive (import, validation groupée).',
      ],
      linkTo: '/admin/dashboard',
      linkLabel: 'Tableau de bord',
    },
    {
      id: 'inscriptions',
      title: 'Inscriptions',
      icon: 'assignment',
      summary:
        'Workflow des demandes apprenants : liste en attente, inscriptions actives, validation/refus et contrôle des prérequis (paiement, dossier, parrainage).',
      steps: [
        'Onglet En attente : triez par date ; ouvrez chaque dossier et vérifiez paiement (module Paiements ou fiche apprenant).',
        'Contrôlez le dossier documentaire (module Dossiers) : pièces obligatoires validées ou en cours.',
        'Vérifiez la cohorte : places disponibles, dates compatibles.',
        'Validez : l’inscription passe en Active ; l’apprenant reçoit une notification et accède aux cours.',
        'Refusez avec motif clair si paiement frauduleux, dossier incomplet ou cohorte fermée — le motif aide le support apprenant.',
        'Onglet Actives : recherchez par nom/téléphone pour suivre ou suspendre une inscription si nécessaire.',
      ],
      tips: ['Ne validez jamais sans trace de paiement ou accord de direction pour prise en charge.'],
      linkTo: '/admin/inscriptions',
      linkLabel: 'Inscriptions',
    },
    {
      id: 'paiements',
      title: 'Paiements',
      icon: 'account_balance',
      summary:
        'Encaissements, recettes par période, transactions en échec, relances et projections de trésorerie.',
      steps: [
        'Consultez la synthèse du jour / semaine / mois : montants encaissés par mode (Mobile Money, carte…).',
        'Ouvrez le détail d’une transaction : référence opérateur, référence plateforme, apprenant, inscription liée.',
        'Transaction « en attente » : attendez la callback opérateur ou lancez une synchronisation si l’outil est disponible.',
        'Relance apprenant : utilisez l’action de relance (notification + email) pour les inscriptions impayées après délai.',
        'Projections : analysez les échéances à venir pour la trésorerie du centre.',
        'Exportez ou notez les références en cas de litige avec un opérateur mobile.',
      ],
      linkTo: '/admin/paiements',
      linkLabel: 'Paiements',
    },
    {
      id: 'apprenants',
      title: 'Apprenants',
      icon: 'group',
      summary:
        'Répertoire global des comptes apprenants : recherche, fiche détaillée, inscriptions multiples, historique notifications et actions rapides.',
      steps: [
        'Recherchez par nom, prénom, téléphone, email ou matricule via la barre de recherche globale ou locale.',
        'Ouvrez une fiche : onglets profil, inscriptions, paiements, documents, notifications récentes.',
        'Depuis la fiche, accédez aux actions courantes (relance paiement, reset mot de passe selon droits).',
        'Vérifiez les doublons (même téléphone) avant de créer manuellement un compte.',
        'Utilisez la fiche comme source unique de vérité lors d’un appel support.',
      ],
      linkTo: '/admin/apprenants',
      linkLabel: 'Apprenants',
    },
    {
      id: 'activites-apprenants',
      title: 'Activités apprenants',
      icon: 'history',
      summary:
        'Journal horodaté des actions métier côté apprenant (inscription, paiement, dépôt document, évaluation…) — distinct du journal de sécurité admin.',
      steps: [
        'Filtrez par catégorie (inscription, paiement, document…) ou recherchez par nom / téléphone.',
        'Cliquez une ligne pour le détail : payload résumé, date, inscription concernée.',
        'Utilisez ce journal pour répondre à « qu’a fait l’apprenant le jour X » sans interroger la base brute.',
        'Export ou pagination : parcourez les pages pour les périodes longues.',
        'Croisez avec Activités formateurs si un litige implique une session ou une note.',
      ],
      linkTo: '/admin/activites-apprenants',
      linkLabel: 'Journal apprenants',
    },
    {
      id: 'formations',
      title: 'Formations',
      icon: 'school',
      summary:
        'Gestion des filières et cohortes : création, capacité, tarifs liés, ouverture/fermeture des inscriptions en ligne.',
      steps: [
        'Sélectionnez une filière dans la liste ; consultez ou modifiez libellé, description, durée, domaine.',
        'Cohortes : créez une cohorte (code, dates début/fin, capacité max) ; liez-la à la filière.',
        'Ouvrez / fermez les inscriptions : une cohorte fermée disparaît du catalogue apprenant.',
        'Vérifiez la cohérence avec Tarification avant d’annoncer un nouveau tarif public.',
        'Après modification majeure, testez le parcours apprenant (catalogue → inscription) en navigation privée.',
      ],
      linkTo: '/admin/formations',
      linkLabel: 'Formations',
    },
    {
      id: 'planning',
      title: 'Planning',
      icon: 'calendar_month',
      summary:
        'Calendrier des vacations : créneaux, salles, cohortes, affectation des formateurs et publication vers le portail formateur.',
      steps: [
        'Naviguez par semaine ou mois ; créez une session (date, heure début/fin, cohorte, salle ou lien visio).',
        'Assignez un ou plusieurs formateurs ; seuls les assignés voient la session dans leur portail.',
        'Modifiez ou annulez une session : les formateurs et apprenants concernés doivent être prévenus (module Communication).',
        'Évitez les chevauchements de salle ou de formateur — le calendrier affiche les conflits si configuré.',
        'Après publication, vérifiez côté formateur (compte test) que la session apparaît.',
      ],
      linkTo: '/admin/planning',
      linkLabel: 'Planning',
    },
    {
      id: 'charge-horaire',
      title: 'Charge horaire',
      icon: 'event',
      summary:
        'Grille des vacations et affectation des formateurs par cohorte pour tout le parcours de formation (indépendant des dates de séance).',
      steps: [
        'Consultez la grille (comme le planning) pour voir formations, salles et créneaux.',
        'Dans le tableau par formation, assignez un formateur à chaque cohorte — valable sur tout le parcours.',
        'L’affectation met à jour automatiquement les sessions liées à la cohorte dans le planning.',
        'Pour créer des sessions ou assigner une cohorte à un créneau, utilisez le planning vacations.',
      ],
      linkTo: '/admin/charge-horaire',
      linkLabel: 'Charge horaire',
    },
    {
      id: 'formateurs',
      title: 'Formateurs',
      icon: 'cast_for_education',
      summary:
        'Annuaire formateurs : création de compte, spécialité, documents, statut actif/inactif et lien avec le planning.',
      steps: [
        'Créez un formateur : téléphone/email, mot de passe temporaire ou invitation ; il complétera son profil à la 1re connexion.',
        'Renseignez spécialité et contrat pour le filtrage dans Planning et Rémunération.',
        'Désactivez un formateur parti du centre : il ne verra plus les nouvelles sessions (historique conservé).',
        'Ouvrez la fiche pour voir sessions animées, évaluations, documents du dossier formateur.',
      ],
      linkTo: '/admin/formateurs',
      linkLabel: 'Formateurs',
    },
    {
      id: 'activites-formateurs',
      title: 'Activités formateurs',
      icon: 'history',
      summary:
        'Traçabilité des actions formateurs : profil, documents, présences saisies, notes, clôtures de session.',
      steps: [
        'Filtrez par catégorie ou formateur ; lisez la chronologie.',
        'Ouvrez le détail pour voir l’action exacte (ex. clôture session cohorte X le jj/mm).',
        'Utile pour audit pédagogique ou contestation de rémunération.',
        'Ne confondez pas avec Sécurité admin qui trace les actions des administrateurs.',
      ],
      linkTo: '/admin/activites-formateurs',
      linkLabel: 'Journal formateurs',
    },
    {
      id: 'academique',
      title: 'Académique',
      icon: 'menu_book',
      summary:
        'Paramètres académiques : barèmes de notation, périodes, publication de résultats, présences consolidées.',
      steps: [
        'Configurez les barèmes (sur 10, sur 20) et les coefficients par type d’évaluation.',
        'Définissez les périodes d’évaluation ou trimestres si le module le prévoit.',
        'Publiez les résultats : les apprenants reçoivent une notification.',
        'Contrôlez les anomalies (notes > barème, présences non saisies) avant publication officielle.',
      ],
      linkTo: '/admin/academique',
      linkLabel: 'Académique',
    },
    {
      id: 'elearning',
      title: 'E-learning',
      icon: 'play_circle',
      summary:
        'Bibliothèque numérique : cours, ressources (vidéo, PDF, exercice), tags, forums, liaison cohortes/formateurs.',
      steps: [
        'Structurez par cours ou filière ; créez des modules et des ressources.',
        'Liez chaque ressource aux cohortes autorisées — une ressource non liée est invisible côté apprenant.',
        'Assignez un formateur référent pour la correction et la modération forum.',
        'Tags : uniformisez le vocabulaire pour faciliter la recherche apprenant.',
        'Modérez les forums signalés ; masquez les messages inappropriés.',
        'Testez la visionneuse PDF et la lecture vidéo avec un compte apprenant test.',
      ],
      linkTo: '/admin/elearning',
      linkLabel: 'E-learning',
    },
    {
      id: 'excellence',
      title: 'Excellence',
      icon: 'emoji_events',
      summary:
        'Programme excellence : périodes, critères de classement, shortlists, orientations et notifications aux apprenants retenus.',
      steps: [
        'Créez une période (dates début/fin de calcul, critères : moyenne min, assiduité min).',
        'Lancez le calcul ou importez les scores ; vérifiez la shortlist générée.',
        'Proposez une orientation (stage, partenaire) : notification + email partent aux apprenants sélectionnés.',
        'Publiez le classement public seulement après validation direction.',
        'Archivez les périodes passées pour l’historique.',
      ],
      linkTo: '/admin/excellence',
      linkLabel: 'Excellence',
    },
    {
      id: 'documents',
      title: 'Dossiers',
      icon: 'folder_shared',
      summary:
        'Validation des pièces administratives apprenants : contrôle, acceptation, refus motivé, demande de nouvelle version.',
      steps: [
        'File d’attente : triez par ancienneté ; ouvrez chaque document en visionneuse.',
        'Vérifiez lisibilité, type de pièce correct, correspondance nom/prénom avec l’inscription.',
        'Validez ou refusez avec motif explicite (ex. « Photo floue », « Pièce expirée »).',
        'L’apprenant reçoit une notification et corrige depuis Mon dossier.',
        'Ne validez pas une pièce d’identité illisible — risque légal pour le centre.',
      ],
      linkTo: '/admin/documents',
      linkLabel: 'Validation dossiers',
    },
    {
      id: 'logistique',
      title: 'Logistique',
      icon: 'inventory_2',
      summary:
        'Patrimoine et équipements : inventaire, affectation salles, statuts (disponible, en panne, maintenance).',
      steps: [
        'Consultez l’inventaire : matériel, quantité, emplacement.',
        'Créez ou modifiez une fiche équipement ; changez le statut (ex. en panne → maintenance).',
        'Liez le matériel aux salles si le module le permet.',
        'Planifiez la maintenance récurrente pour éviter les pannes en session examen.',
      ],
      linkTo: '/admin/logistique',
      linkLabel: 'Logistique',
    },
    {
      id: 'communication',
      title: 'Communication',
      icon: 'notifications_active',
      summary:
        'Campagnes notification push, SMS, emails ciblés par audience (apprenants, formateurs, cohorte, impayés…).',
      steps: [
        'Choisissez le canal (push, SMS, email) selon l’urgence et le budget.',
        'Définissez l’audience : tous les apprenants, une cohorte, les impayés, les formateurs actifs…',
        'Rédigez titre + message clairs ; prévisualisez si disponible.',
        'Planifiez l’envoi ou lancez immédiatement ; consultez les statistiques d’envoi / échec.',
        'Évitez les doublons : une relance paiement + SMS le même jour peut gêner — espacez les campagnes.',
      ],
      linkTo: '/admin/communication',
      linkLabel: 'Communication',
    },
    {
      id: 'security',
      title: 'Sécurité',
      icon: 'shield',
      summary:
        'Audit des actions administrateurs, paramètres JWT/secrets, sauvegardes, volume d’événements et accès au détail de chaque action sensible.',
      steps: [
        'Consultez le compteur d’actions sur la période (7, 30, 90 jours).',
        'Ouvrez le journal : filtrez par administrateur, type d’action, date.',
        'Cliquez une entrée pour le détail complet (avant/après, IP, métadonnées).',
        'Vérifiez les indicateurs de configuration (secrets JWT, sauvegardes) — alerte si rouge.',
        'En cas d’action suspecte, identifiez le compte admin et révoquez ses accès via la procédure interne.',
        'Croisez avec Activités apprenants/formateurs pour les litiges métier, pas seulement techniques.',
      ],
      linkTo: '/admin/security',
      linkLabel: 'Sécurité',
    },
    {
      id: 'parrainage',
      title: 'Parrainage',
      icon: 'groups',
      summary:
        'Supervision des parrainages individuels et institutionnels : codes, filleuls, commissions, litiges et partenaires.',
      steps: [
        'Liste des parrains actifs : apprenants, formateurs, partenaires institutionnels.',
        'Pour un litige « code oublié » : recherchez l’inscription filleul et vérifiez la date de saisie du code.',
        'Ajustez manuellement une commission seulement avec accord direction et trace dans le journal.',
        'Désactivez un code frauduleux ou spammé.',
        'Alignez les règles avec le module Tarification (bonus, paliers).',
      ],
      linkTo: '/admin/parrainage',
      linkLabel: 'Parrainage',
    },
    {
      id: 'wallet',
      title: 'Portefeuilles',
      icon: 'account_balance_wallet',
      summary:
        'Soldes commissions et traitement des demandes de retrait apprenants / formateurs / partenaires.',
      steps: [
        'File des retraits en attente : ouvrez chaque demande (montant, bénéficiaire, coordonnées paiement).',
        'Vérifiez le solde disponible ≥ montant demandé et l’absence de litige parrainage.',
        'Validez après virement effectif ; refusez avec motif si coordonnées invalides.',
        'Consultez l’historique global pour audit financier.',
        'Ne validez jamais un retrait sans preuve de paiement sortant (référence virement / Mobile Money).',
      ],
      linkTo: '/admin/wallet',
      linkLabel: 'Portefeuilles',
    },
    {
      id: 'tarifs',
      title: 'Tarification',
      icon: 'payments',
      summary:
        'Grilles tarifaires : frais d’inscription par formation, rémunération formateur, bonus parrainage et règles associées.',
      steps: [
        'Onglet Formations : mettez à jour montants et devises ; enregistrez ; vérifiez côté catalogue apprenant.',
        'Onglet Formateurs : taux horaire, forfait vacation — impacte Rémunération formateur.',
        'Onglet Parrainage : bonus fixe ou pourcentage, paliers ; testez avec un cas filleul fictif.',
        'Toute modification majeure doit être communiquée (module Communication) avant date d’effet.',
      ],
      linkTo: '/admin/tarifs',
      linkLabel: 'Tarification',
    },
    {
      id: 'site',
      title: 'Site & QR',
      icon: 'public',
      summary:
        'Contenu du site public (textes, visuels, filières mises en avant) et génération de QR codes parrainage / inscription.',
      steps: [
        'Modifiez les textes institutionnels ; enregistrez ; vérifiez sur le site public en navigation privée.',
        'Mettez à jour les visuels (bandeaux, logos) selon charte graphique.',
        'Générez des QR pour campagnes parrainage : associez le bon code partenaire ou institutionnel.',
        'Testez chaque QR avec un téléphone avant diffusion print.',
      ],
      linkTo: '/admin/site',
      linkLabel: 'Site public',
    },
    {
      id: 'settings',
      title: 'Devise & taux',
      icon: 'settings',
      summary:
        'Devise principale du centre, taux de change affichés et paramètres financiers globaux impactant paiements et tarifs.',
      steps: [
        'Consultez la devise active (ex. CDF) et les taux secondaires (USD…) si multi-devises.',
        'Mettez à jour le taux avec date d’effet ; les nouvelles inscriptions utiliseront le nouveau taux.',
        'Après changement, vérifiez Paiements et Tarification pour cohérence des montants affichés.',
        'Documentez en interne tout changement de taux pour la comptabilité.',
      ],
      linkTo: '/admin/settings',
      linkLabel: 'Paramètres devise',
    },
  ],
  faqs: [
    {
      question: 'Un paiement est validé côté opérateur mais pas sur la plateforme.',
      answer:
        '1) Retrouvez la référence opérateur dans Paiements ou Activités apprenants. 2) Vérifiez le statut (en attente, échec callback). 3) Relancez la synchronisation ou réconciliation si disponible. 4) En dernier recours, enregistrez manuellement avec trace audit et pièce justificative opérateur.',
    },
    {
      question: 'Comment réinitialiser le mot de passe d’un utilisateur ?',
      answer:
        'Orientez l’utilisateur vers Mot de passe oublié sur la page publique. Pour un blocage urgent, utilisez les outils de la fiche apprenant/formateur ou le module Sécurité selon vos droits — documentez l’action dans le journal audit.',
    },
    {
      question: 'Une inscription validée n’affiche pas les cours e-learning.',
      answer:
        'Vérifiez : inscription Active, ressources liées à la cohorte dans E-learning, formateur assigné. Testez avec le compte apprenant concerné. Publiez ou liez les ressources manquantes.',
    },
    {
      question: 'Comment traiter un retrait portefeuille suspect ?',
      answer:
        'Mettez en pause (refus motivé), vérifiez historique parrainage, identité du bénéficiaire et doublons de demande. Escaladez direction si montant élevé ou pattern frauduleux.',
    },
  ],
};
